import { type WritableAtom, useAtom } from "jotai";
import { RESET, atomWithStorage, useResetAtom } from "jotai/utils";
import { atomFamily } from "jotai-family";
import cloneDeep from "lodash-es/cloneDeep";
import compact from "lodash-es/compact";
import isEmpty from "lodash-es/isEmpty";
import keys from "lodash-es/keys";
import { useCallback, useMemo, useRef } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import { ObjectFilterMetadataResponseModel } from "@/api";
import { SelectData } from "@/components/Kendo/Form/FinalForm";
import { FILTER_STATE_KEY } from "@/configs/constants";

type Filters = { [key: string]: SelectData[] };

export interface FilterState {
  defaultParams: Filters;
  filters: Filters;
  subFilters?: ObjectFilterMetadataResponseModel[];
  apply: boolean;
}

export type FilterStore = {
  reset: () => void;
  onChange: (FilterState: FilterState) => void;
  state: FilterState;
};

type FilterStateAtom = WritableAtom<
  FilterState,
  [
    | FilterState
    | typeof RESET
    | ((prev: FilterState) => FilterState | typeof RESET),
  ],
  void
>;

/**
 * Drops `null`/`undefined` members from every category's value list.
 *
 * A category is removed only when compaction emptied it. A category the user
 * explicitly cleared legitimately holds `[]`, and dropping that would make a
 * page's `defaultFilters` re-apply on the next mount.
 */
const sanitizeFilters = (filters: Filters = {}): Filters =>
  Object.entries(filters).reduce<Filters>((result, [key, values]) => {
    const cleaned = compact(values);
    if (cleaned.length > 0 || isEmpty(values)) result[key] = cleaned;
    return result;
  }, {});

export const useState = (
  filterStateAtom: FilterStateAtom,
  defaults: Filters,
) => {
  const resetToInitial = useResetAtom(filterStateAtom);
  const [rawState, setRawState] = useAtom(filterStateAtom);

  // `defaults` is typically an inline object literal at the call site (e.g. PolicyAuditTable's
  // `defaultParams`), recreated every render - read through a ref instead of a dependency so
  // `reset` keeps a stable identity like every other value this hook returns. Consumers such as
  // `QueryBar`'s `restFilters` effect list `reset` as a dependency; a `reset` that churned every
  // render would re-run that effect every render too, and if it ever called `reset()` on mount
  // (as `restFilters` handling does), that becomes a render loop, not just wasted work.
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  // Resetting to the atom's static initial value (`defaultParams: {}`) would drop page context a
  // consumer supplied through `defaults` (e.g. PolicyAuditTable's applicationId) with nothing to
  // restore it afterward - the effect below only re-seeds `defaultParams` when `defaults` itself
  // changes, which it never does for a value like applicationId, so every lookup on the bar would
  // silently lose it for the rest of the page's life. Reapply the current `defaults` in the same
  // write instead of resetting blind.
  const reset = useCallback(() => {
    const currentDefaults = defaultsRef.current;
    if (isEmpty(currentDefaults)) {
      resetToInitial();
    } else {
      setRawState({
        filters: {},
        apply: false,
        defaultParams: currentDefaults,
      });
    }
  }, [resetToInitial, setRawState]);

  // This state is persisted to localStorage, so a hole written by an older
  // build outlives the bug that wrote it — a page seeding a default from an API
  // value it turned out not to have stored `{ accountId: [null] }`, and that
  // entry is read back long after the page stopped producing it. Scrub it on
  // read: consumers dereference these values (`v.name`, `v.id`),
  // `useFilterParams` turns them into query params, and `isEmpty(state.filters)`
  // is what decides whether a page's `defaultFilters` still apply — a stale
  // hole breaks all three, and the last one silently forever.
  const state = useMemo<FilterState>(
    () => ({ ...rawState, filters: sanitizeFilters(rawState.filters) }),
    [rawState],
  );

  // Writes go through the same scrub, for two reasons. An updater would
  // otherwise read a hole straight out of `prev` — `onRemove` below filters the
  // raw list by `item.id` — and the spread-the-whole-map updaters
  // (`resetDepended`, `onSubFilterUpdate`, `onRemoveSubFilter`) would
  // re-persist a hole on every unrelated edit. Scrubbing here instead lets a
  // poisoned localStorage entry heal on the next write.
  const setState = useCallback<typeof setRawState>(
    update =>
      setRawState(prev => {
        const scrubbed = { ...prev, filters: sanitizeFilters(prev.filters) };
        const next = typeof update === "function" ? update(scrubbed) : update;
        if (next === RESET) return next;
        return { ...next, filters: sanitizeFilters(next.filters) };
      }),
    [setRawState],
  );

  useDeepCompareEffect(() => {
    if (isEmpty(defaults)) return;
    setState(currentState => {
      return {
        ...currentState,
        defaultParams: defaults,
      };
    });
  }, [defaults]);

  const onChange = useCallback(
    (key: string, value: SelectData[]) => {
      setState(currentState => {
        return {
          ...currentState,
          filters: { ...currentState.filters, [key]: value },
        };
      });
    },
    [setState],
  );

  const resetDepended = useCallback(
    (index: number) => {
      setState(currentState => {
        const filters = { ...currentState.filters };

        const filterKeys = keys(filters).slice(index + 1);
        filterKeys.forEach(name => {
          delete filters[name];
        });
        return {
          ...currentState,
          filters,
        };
      });
    },
    [setState],
  );

  const onRemove = useCallback(
    (key: string, value: SelectData) => {
      setState(currentState => {
        const filterItems = (currentState.filters?.[key] || []).filter(
          (item: SelectData) => item.id !== value.id,
        );

        return {
          ...currentState,
          filters: { ...currentState.filters, ...{ [key]: filterItems } },
        };
      });
    },
    [setState],
  );

  const onSubFilterAdd = useCallback(() => {
    setState(currentState => {
      const subFilters = currentState.subFilters || [];
      const newSubFilters = [...subFilters, {}];
      return {
        ...currentState,
        subFilters: newSubFilters,
      };
    });
  }, [setState]);

  const onSubFilterUpdate = useCallback(
    (index: number, filter: ObjectFilterMetadataResponseModel) => {
      setState(currentState => {
        const subFilters = [...(currentState.subFilters || [])];
        const existingFilter = subFilters[index];
        subFilters[index] = filter;
        const filters = cloneDeep(currentState.filters);
        if (existingFilter.name !== filter.name) {
          delete filters[existingFilter.name!];
        }
        return {
          ...currentState,
          filters,
          subFilters,
        };
      });
    },
    [setState],
  );

  const onRemoveSubFilter = useCallback(
    (key: string) => {
      setState(currentState => {
        const filters = { ...currentState.filters };
        const subFilters = currentState.subFilters?.filter(
          item => item.name !== key,
        );
        delete filters[key];
        return {
          ...currentState,
          filters,
          subFilters,
        };
      });
    },
    [setState],
  );

  const changeApply = useCallback(
    (value: boolean) => {
      setState(currentState => {
        return {
          ...currentState,
          apply: value,
        };
      });
    },
    [setState],
  );

  const applyDefaultFilters = useCallback(
    (
      filters: FilterState["filters"],
      data: ObjectFilterMetadataResponseModel[] = [],
      onChange?: (filters: FilterState["filters"]) => void,
    ) => {
      setState(currentState => {
        const subFilters: ObjectFilterMetadataResponseModel[] = [];
        keys(filters).forEach(filter => {
          const dataItem = data.find(item => item.name === filter);
          if (dataItem) {
            subFilters.push(dataItem);
          }
        });

        onChange?.(filters);

        return {
          ...currentState,
          filters,
          subFilters,
          apply: true,
        };
      });
    },
    [setState],
  );

  return {
    applyDefaultFilters,
    reset,
    resetDepended,
    state,
    onChange,
    onRemove,
    onSubFilterAdd,
    onSubFilterUpdate,
    onRemoveSubFilter,
    changeApply,
  };
};

/**
 * Clears all entries in the local storage that start with the specified key prefix.
 *
 * @param localStorageEffectKey - The prefix of the keys to be removed from local storage.
 */
const clearLocalStorageEffectData = (localStorageEffectKey: string) => {
  const keys = Object.keys(localStorage);
  keys.forEach(key => {
    if (key.startsWith(localStorageEffectKey)) {
      localStorage.removeItem(key);
    }
  });
};

const defaultFilterState: FilterState = {
  defaultParams: {},
  filters: {},
  apply: false,
};

const getStorageKey = (uniqueKey: string) =>
  `${FILTER_STATE_KEY}__${JSON.stringify(uniqueKey)}`;

export const filterState = atomFamily<string, FilterStateAtom>(uniqueKey =>
  atomWithStorage<FilterState>(
    getStorageKey(uniqueKey),
    defaultFilterState,
    undefined,
    { getOnInit: true },
  ),
);

/**
 * Clears the filter state by removing the associated data from local storage.
 *
 * This function calls `clearLocalStorageEffectData` with the `FILTER_STATE_KEY`
 * to remove any stored filter state data from the local storage.
 */
export const clearFilterState = () => {
  clearLocalStorageEffectData(FILTER_STATE_KEY);
};

export const useStore = (uniqueKey: string, defaults: Filters = {}) => {
  return useState(filterState(uniqueKey), defaults);
};
